import {AfterViewInit, Component, Input, OnInit, ViewChild} from '@angular/core';
import {MatPaginator as MatPaginatorNew} from "@angular/material/paginator";
import {MatTableDataSource} from "@angular/material/table";
import {hasPermission, User} from "../../../model/user";
import {EMPTY, Observable, of} from "rxjs";
import {MatDialog} from "@angular/material/dialog";
import {UsersService} from "../../../services/users.service";
import {AuthService} from "../../../services/auth.service";
import {DialogsService} from "../../../services/dialogs.service";
import {SnackService} from "../../../services/snack.service";
import {switchMap, tap} from "rxjs/operators";
import {Sort} from "@angular/material/sort";
import {
  GeneralItemDetailDialogComponent
} from "../../../shared-components/general-item-detail-dialog/general-item-detail-dialog.component";
import {merge} from "lodash-es";
import {CreateUserDialogComponent} from "../create-user-dialog/create-user-dialog.component";
import {Testbed} from "../../../model/testbed";
import {Box} from "../../../model/box";
import {UserPermissionsDialogComponent} from "../user-permissions-dialog/user-permissions-dialog.component";
import {BoxesService} from "../../../services/boxes-service";

@Component({
  selector: 'aw-list-users',
  templateUrl: './list-users.component.html',
  styleUrls: ['./list-users.component.scss']
})
export class ListUsersComponent implements OnInit, AfterViewInit {

  @Input() type: 'researchersByBox' | 'testersByTestbed' | 'testersByCompany' | 'usersByCompany' | 'all' = 'all';
  @Input() filterByTestbed?: Testbed;
  @Input() filterByBox?: Box;
  @Input() filterByCompanyId?: number;
  @Input() embeddedLayout = false;

  @Input() deleteAction?: {
    enabled: boolean;
    action?: (userId: number) => Observable<any>;
  } = {enabled: true};

  @Input() detailsAction?: {
    enabled: boolean;
  } = {enabled: true};


  public possibleColumns = [
    'firstName',
    'lastName',
    'email',
    'phone',
    'address',
    'enabled',
    'company',
    'permissions',
    'm2m',
    'action'
  ];
  public displayedColumns: string[] = [];
  disabled = false;
  disabledItemId: number[] = [];
  loading = false;
  enabled = true;
  @ViewChild('paginatorUsers') paginatorUsers!: MatPaginatorNew;
  dataSource!: MatTableDataSource<User>;
  loggedUser: User;
  permissionFilters: string[] = [];
  generalFilter: string = '';
  componentUsageError?: string;
  protected readonly hasPermission = hasPermission;
  private allUsers$!: Observable<User[]>;

  constructor(
    public dialog: MatDialog,
    private usersService: UsersService,
    private boxesService: BoxesService,
    private authService: AuthService,
    private dialogsService: DialogsService,
    private snackService: SnackService
  ) {
  }

  ngOnInit(): void {
    this.loggedUser = this.authService.loggedUser();
  }

  ngAfterViewInit(): void {

    this.allUsers$ =
      of(true).pipe(
        tap(() => this.loading = true),
        switchMap(() => {

          switch (this.type) {
            case "usersByCompany":
              if (!this.filterByCompanyId) {
                this.componentUsageError = 'ListUsersComponent(aw-list-users) usage error type=usersByCompany but no filterByCompanyId=undefined';
                return EMPTY;
              }

              return this.usersService.getAllUsersByCompany(this.filterByCompanyId);

            case "researchersByBox":
              if (!this.filterByBox) {
                this.componentUsageError = 'ListUsersComponent(aw-list-users) usage error type=researchersByBox but no filterByBox=undefined';
                return EMPTY;
              }

              return this.usersService.getBoxUsers(this.filterByBox.id);

            case "testersByTestbed":
              if (!this.filterByTestbed) {
                this.componentUsageError = 'ListUsersComponent(aw-list-users) usage error type=testersByTestbed but no filterByTestbed=undefined';
                return EMPTY;
              }

              return this.usersService.getAllTestersByTestbed(this.filterByTestbed?.id);

            case "all":

              const isOnlyTester = hasPermission(this.loggedUser, 'tester') &&
                !hasPermission(this.loggedUser, 'researcher') &&
                !hasPermission(this.loggedUser, 'admin') &&
                !hasPermission(this.loggedUser, 'customer') &&
                !hasPermission(this.loggedUser, 'logistic');

              if (isOnlyTester) {
                const companyIdTester = this.loggedUser?.company?.id;
                if (!companyIdTester) {
                  this.componentUsageError = 'Tester user has no associated company';
                  return EMPTY;
                }
                return this.usersService.getAllTestersByCompany(companyIdTester);
              }

          }


          return this.usersService.getUsers();

        }),
        tap(users => {
          this.setDynamicColumns(users);
          this.dataSource = new MatTableDataSource<User>(users);

          this.dataSource.filterPredicate = this.customFilterPredicate();

          this.dataSource.paginator = this.paginatorUsers;
          this.loading = false;
        })
      );

    this.allUsers$.subscribe();
  }

  addPermissionFilter(value: string): void {
    const clean = (value || '').trim().toLowerCase();
    if (!clean) return;
    if (!this.permissionFilters.includes(clean)) {
      this.permissionFilters.push(clean);
    }
    this.dataSource.filter = this.generalFilter + ' ';
  }

  removePermissionFilter(value: string): void {
    const idx = this.permissionFilters.indexOf(value);
    if (idx >= 0) {
      this.permissionFilters.splice(idx, 1);
    }
    this.dataSource.filter = this.generalFilter + ' ';
  }

  applyFilters(event: string): void {
    this.generalFilter = (event || '').trim().toLowerCase();
    this.dataSource.filter = this.generalFilter;
  }

  doSortUsers(event: Sort): void {
    this.dataSource.paginator?.firstPage();
    this.dataSource.data = this.dataSource.data.sort((a, b) => {
        const index: string = event.active;
        if (event.direction === 'asc') {
          return ('' + (b as any)[index]).localeCompare('' + (a as any)[index]);
        } else {
          return ('' + (a as any)[index]).localeCompare('' + (b as any)[index]);
        }

      }
    );
  }

  details(user: User): void {
    this.dialog.open(GeneralItemDetailDialogComponent, {data: user});
  }

  createUser(): void {
    this.disabled = true;
    this.dialog
      .open(CreateUserDialogComponent, {data: new User()})
      .afterClosed()
      .pipe(switchMap(value => value ? this.allUsers$ : EMPTY))
      .subscribe(
        () => {
          this.disabled = false;
        },
        (error) => {
          this.snackService.error(error.message);
          this.disabled = false;
        }, () => {
          this.disabled = false;
        });
  }

  editUser(user: User): void {
    this.disabled = true;
    this.dialog
      .open(CreateUserDialogComponent, {data: user})
      .afterClosed()
      .pipe(switchMap(value => value ? this.allUsers$ : EMPTY))
      .subscribe(
        () => {
          this.disabled = false;
        },
        (error) => {
          this.snackService.error(error.message);
          this.disabled = false;
        }, () => {
          this.disabled = false;
        });
  }

  editPermissions(user: User): void {
    this.disabled = true;
    this.dialog
      .open(UserPermissionsDialogComponent, {data: user})
      .afterClosed()
      .pipe(switchMap(value => value ? this.allUsers$ : EMPTY))
      .subscribe(
        () => {
          this.disabled = false;
        },
        (error) => {
          this.snackService.error(error.message);
          this.disabled = false;
        }, () => {
          this.disabled = false;
        });
  }

  deleteUser(user: User): void {
    this.disabled = true;
    const id = user.id ?? -1;
    this.disabledItemId.push(id);
    this.dialogsService
      .confirm('Remove User', `Are you sure you want to remove the user "${user.firstName} ${user.lastName}"?`)
      .pipe(
        switchMap((value) => {
          if (!value)
            return EMPTY;

          return (this.deleteAction && this.deleteAction.action) ?
            this.deleteAction.action(user.id) :
            this.usersService.deleteUser(user.id);
        }),
        switchMap(() => this.allUsers$)
      )
      .subscribe(() => {
          this.disabled = false;
          this.snackService.success('User deleted!');
          this.disabledItemId.splice(this.disabledItemId.indexOf(id), 1);
        }, (error) => {
          this.snackService.error(error.message);
          this.disabled = false;
          this.disabledItemId.splice(this.disabledItemId.indexOf(id), 1);
        },
        () => {
          this.disabled = false;
          this.disabledItemId.splice(this.disabledItemId.indexOf(id), 1);
        });

  }

  changeUserHabilitation(user: User): void {
    this.disabled = true;
    const editedUser = merge(new User(), user);
    editedUser.enabled = !user.enabled;
    const keyword = editedUser.enabled ? 'enable' : 'disable';
    this.dialogsService
      .confirm(`${keyword.toUpperCase()} user`, `Are you sure you want to ${keyword} the user "${user.firstName} ${user.lastName}"?`)
      .pipe(
        switchMap((value) => value ? this.usersService.updateUser(editedUser) : EMPTY),
        switchMap(() => this.allUsers$)
      )
      .subscribe(() => {
          this.snackService.success(`User ${keyword}d!`);
          this.disabled = false;
        },
        (error) => {
          this.snackService.error(error.message);
          this.disabled = false;
        },
        () => {
          this.disabled = false;
        });
  }

  changeUserM2MHabilitation(user: User): void {
    this.disabled = true;
    const editedUser = merge(new User(), user);
    editedUser.m2mEnabled = !user.m2mEnabled;
    const keyword = editedUser.m2mEnabled ? 'enable' : 'disable';
    this.dialogsService
      .confirm(`${keyword.toUpperCase()} M2M`,
        `Are you sure you want to ${keyword} m2m access for the user "${user.firstName} ${user.lastName}"?`)
      .pipe(
        switchMap((value) => value ? this.usersService.updateUser(editedUser) : EMPTY),
        switchMap(() => this.allUsers$)
      )
      .subscribe(() => {
          this.snackService.success(`m2m access ${keyword}d!`);
          this.disabled = false;
        },
        (error) => {
          this.snackService.error(error.message);
          this.disabled = false;
        },
        () => {
          this.disabled = false;
        });
  }

  private setDynamicColumns(users: User[]): void {
    const cols = new Set<string>();

    for (const user of users) {
      for (const key of Object.keys(user)) {
        if (this.possibleColumns.includes(key)) cols.add(key);
      }
      if (user.company?.companyName) cols.add('company');
    }

    // cols.add('permissions');
    if (this.type == 'all')
      cols.add('m2m');

    cols.add('action');

    this.displayedColumns = this.possibleColumns.filter(c => cols.has(c));
  }

  private customFilterPredicate() {
    return (data: User, filter: string): boolean => {

      const accumulator = (currentTerm: string, key: string) => {
        const value = (data as any)[key];

        if (key === 'permissions' && Array.isArray(value)) {
          return currentTerm + ' ' + value.map(p => p.name).join(' ').toLowerCase();
        }

        if (key === 'company' && data.company?.companyName) {
          return currentTerm + ' ' + data.company.companyName.toLowerCase();
        }

        return currentTerm + ' ' + String(value ?? '').toLowerCase();
      };


      const dataStr = Object.keys(data)
        .reduce(accumulator, '')
        .trim()
        .toLowerCase();

      const matchesGeneral = dataStr.includes((filter || '').toLowerCase());

      if (this.permissionFilters && this.permissionFilters.length > 0) {
        const userPerms = (data.permissions ?? []).map((p: any) => String(p.name).toLowerCase());
        const matchesPermissions = this.permissionFilters.every(f =>
          userPerms.some(up => up.includes(f))
        );

        return matchesGeneral && matchesPermissions;
      }

      return matchesGeneral;
    };
  }
}
